import { Modal } from "react-native";
import type { ModalProps } from "react-native";

import { useModalTracker } from "@/hooks/useModalTracker";
import { useSplashScreen } from "@/hooks/useSplashScreen";
import useModalCrashAndFreezeSafety from "@/ui/composed/modal/SafeNativeModal/useModalCrashAndFreezeSafety";

/**
 * A react-native Modal that is aware of the SplashScreen and whether the native modals *should* be hidden
 * (eg, if an expo-style Modal is routed to, which covers the RN Modal, permanently freezing it)
 *
 * Listeners prevent the modal from being rendered when the splash screen is visible
 * and also when we declaratively hide native modals (i.e., for authentication timeout flows)
 * @param {ModalProps} props - Modal props
 * @param {React.ReactNode} props.children - Modal children
 * @returns Modal
 */
const SafeNativeModal = ({ children, ...props }: ModalProps) => {
  const { onDismiss, visible: isVisibleParentProp } = props;
  const { isSplashScreenVisible } = useSplashScreen();
  const { isSafeToRenderNativeModals } = useModalTracker();

  // when the SplashScreen appears it is meant to "hide all app content",
  // however RN Modals would stay overlaid on top of the SplashScreen
  // thus they explicitly have to unrender themselves when isSplashScreenVisible
  const isVisibilityRequested = Boolean(
    isVisibleParentProp && !isSplashScreenVisible && isSafeToRenderNativeModals,
  );

  const { handleDismiss, isVisible } = useModalCrashAndFreezeSafety({
    isVisibilityRequested,
    onDismiss,
  });

  return (
    <Modal
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
      onDismiss={handleDismiss}
      // NOTE: do not modify the key prop to try to enforce safety against various Modal bugs
      // see: https://github.com/equiphealth/my-equip-mobile/pull/1073#discussion_r2866808336 (JN-2162/JN-2193 bug)
      visible={isVisible}
    >
      {children}
    </Modal>
  );
};

export default SafeNativeModal;
